import { DateTime } from 'luxon';

import { ITimesheet, ITimesheetEntry, KekaDayTypePresentation } from '@core/interfaces';

/**
 * Generates 7 empty timesheet entries starting from the given week start date.
 * Used when OCR fails and user needs to enter hours manually.
 */
export function generateEmptyWeekEntries(
  weekStart: string,
): Omit<ITimesheetEntry, 'id' | 'timesheet_id' | 'created_at' | 'updated_at'>[] {
  const start = DateTime.fromISO(weekStart);
  return Array.from({ length: 7 }, (_, i) => ({
    date: start.plus({ days: i }).toISODate()!,
    extracted_hours: 0,
    submitted_hours: 0,
    is_modified: false,
  }));
}

/**
 * Generates week day metadata for table columns.
 */
export function generateWeekDays(
  weekStart: string | DateTime,
): { day: string; date: string; label: string }[] {
  const start = DateTime.isDateTime(weekStart) ? weekStart : DateTime.fromISO(weekStart);

  return Array.from({ length: 7 }, (_, i) => {
    const currentDate = start.plus({ days: i });
    return {
      day: currentDate.toFormat('cccc').toLowerCase(),
      date: currentDate.toISODate()!,
      label: currentDate.toFormat('ccc dd'),
    };
  });
}

/**
 * Gets an entry for an exact ISO calendar date from a timesheet.
 *
 * A weekday-only lookup can incorrectly show an entry from another week when a
 * source contains more than one week of data.
 */
export function getEntryForDate(
  entries: ITimesheetEntry[],
  date: string,
): ITimesheetEntry | undefined {
  return entries.find(
    (entry) => DateTime.fromISO(entry.date, { setZone: true }).toISODate() === date,
  );
}

const attendanceSources: Record<number, { label: string; icon: string }> = {
  0: { label: 'Device', icon: 'badge' },
  1: { label: 'Web', icon: 'language' },
  2: { label: 'Mobile', icon: 'smartphone' },
  3: { label: 'API', icon: 'api' },
  4: { label: 'Import', icon: 'file_upload' },
  5: { label: 'Continuous Punch', icon: 'schedule' },
  6: { label: 'System Generated', icon: 'settings' },
  7: { label: 'Kiosk', icon: 'desktop_windows' },
  8: { label: 'Slack', icon: 'chat' },
  10: { label: 'Mobile Offline', icon: 'phonelink_off' },
};

const leaveStatuses: Record<number, string> = {
  0: 'Pending',
  1: 'Approved',
  2: 'Rejected',
  3: 'Cancelled',
  4: 'In Approval Process',
};

export function attendanceSource(value: number | null | undefined): {
  label: string;
  icon: string;
} {
  if (value === null || value === undefined) return { label: '—', icon: '' };
  return attendanceSources[value] ?? { label: `Unknown (${value})`, icon: 'help_outline' };
}

export function leaveStatusLabel(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return leaveStatuses[value] ?? `Unknown (${value})`;
}

export function leaveDurationLabel(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : String(value);
}

type LeaveStatusTone = 'warning' | 'success' | 'error' | 'neutral';

export function leaveStatusPresentation(
  value: number | null | undefined,
): { label: string; icon: string; tone: LeaveStatusTone } | null {
  if (value === null || value === undefined) return null;

  const styles: Record<number, { icon: string; tone: LeaveStatusTone }> = {
    0: { icon: 'schedule', tone: 'warning' },
    1: { icon: 'check_circle', tone: 'success' },
    2: { icon: 'cancel', tone: 'error' },
    3: { icon: 'block', tone: 'neutral' },
    4: { icon: 'pending', tone: 'warning' },
  };
  const style = styles[value] ?? { icon: 'help_outline', tone: 'neutral' };
  return { label: leaveStatusLabel(value), ...style };
}

export function leaveDurationPresentation(
  value: number | null | undefined,
): { label: string; icon: string; multiDay: boolean } | null {
  if (value === null || value === undefined) return null;
  const multiDay = value > 1;
  return {
    label: `${value} ${value > 1 || value === 0 ? 'days' : 'day'}`,
    icon: multiDay ? 'date_range' : 'event',
    multiDay,
  };
}

export function approvedLeaveLabel(
  status: number | null | undefined,
  session: number | null | undefined,
): string {
  if (status !== 1) return '';
  if (session === 0) return ', approved first-half leave';
  if (session === 1) return ', approved second-half leave';
  if (session === 2) return ', approved full-day leave';
  return '';
}

/** Inner background of the Keka hours circle for approved leave only. */
export function approvedLeaveBackground(
  status: number | null | undefined,
  session: number | null | undefined,
): string | null {
  if (status !== 1) return null;
  const green = 'var(--mat-sys-primary)';
  const normal = 'var(--mat-sys-surface)';
  if (session === 0) return `linear-gradient(to right, ${green} 50%, ${normal} 50%)`;
  if (session === 1) return `linear-gradient(to right, ${normal} 50%, ${green} 50%)`;
  if (session === 2) return green;
  return null;
}

/** Display treatment for the Keka day types that do not represent a normal workday. */
const kekaDayTypes: Record<number, KekaDayTypePresentation> = {
  2: {
    label: 'Weekly off',
    background: 'var(--mat-sys-tertiary-container)',
    color: 'var(--mat-sys-on-tertiary-container)',
    outline: 'var(--mat-sys-tertiary)',
  },
  1: {
    label: 'Company holiday',
    background: 'var(--mat-sys-secondary-container)',
    color: 'var(--mat-sys-on-secondary-container)',
    outline: 'var(--mat-sys-secondary)',
  },
};

export function kekaDayTypePresentation(
  value: number | null | undefined,
): KekaDayTypePresentation | null {
  if (value === null || value === undefined) return null;
  return kekaDayTypes[value] ?? null;
}

export function kekaDayTypeLabel(value: number | null | undefined): string {
  const presentation = kekaDayTypePresentation(value);
  return presentation ? `, ${presentation.label.toLowerCase()}` : '';
}

/**
 * Keeps approved-leave fills visible when a leave day also has a Keka day type.
 * In that overlap case the day-type colour is applied to the circle outline.
 */
export function kekaHoursBackground(
  dayType: number | null | undefined,
  leaveStatus: number | null | undefined,
  leaveSession: number | null | undefined,
): string | null {
  return (
    approvedLeaveBackground(leaveStatus, leaveSession) ??
    kekaDayTypePresentation(dayType)?.background ??
    null
  );
}

export function kekaHoursTextColor(
  dayType: number | null | undefined,
  leaveStatus: number | null | undefined,
  leaveSession: number | null | undefined,
): string | null {
  if (leaveStatus === 1 && leaveSession === 2) return 'var(--mat-sys-on-primary)';
  if (leaveStatus === 1) return null;
  return kekaDayTypePresentation(dayType)?.color ?? null;
}

export function kekaDayTypeOutline(value: number | null | undefined): string | null {
  return kekaDayTypePresentation(value)?.outline ?? null;
}

/** Percentage of a nine-hour Keka workday, capped for display only. */
export function kekaHoursProgress(hours: number): number {
  if (!Number.isFinite(hours)) return 0;
  return (Math.min(Math.max(hours, 0), 9) / 9) * 100;
}

/** Match actual ISO dates, not just the weekday, to avoid displaying a different week's entry. */
export function getWeeklyEntries(weekStart: string, timesheet: ITimesheet | undefined) {
  const entries = new Map(timesheet?.entries.map((entry) => [entry.date, entry]) ?? []);
  return generateWeekDays(weekStart).map((day) => ({
    ...day,
    entry: entries.get(day.date),
  }));
}

/**
 * Remaps OCR entry dates in each timesheet to start from the given weekStart.
 * Used when the user-selected week_start differs from the model-extracted one,
 * to ensure OCR entries align with the submission's week_start before persisting.
 * Trusted Keka data is date-addressed and can be sparse, so its API dates must
 * not be reassigned by array position.
 */
export function normalizeEntryDatesToWeekStart(
  timesheets: ITimesheet[],
  weekStart: DateTime,
): ITimesheet[] {
  return timesheets.map((ts) => ({
    ...ts,
    entries: ts.source.is_trusted
      ? ts.entries
      : ts.entries.map((entry, i) => ({
          ...entry,
          date: weekStart.plus({ days: i }).toISODate()!,
        })),
  }));
}

/**
 * Calculates total submitted hours for a timesheet.
 */
export function calculateTotalHours(entries: ITimesheetEntry[]): number {
  const total = entries.reduce((sum, entry) => sum + entry.submitted_hours, 0);
  return Number.isInteger(total) ? total : parseFloat(total.toFixed(2));
}
